package fr.frux.core.common.auth

import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

/**
 * Paire atomique de jetons de session (#26, ADR 0019).
 *
 * L'access JWT (court, 15 min) et le refresh opaque rotatif (90 j glissants) ne peuvent pas être
 * lus ou écrits séparément sans risquer un état incohérent — un refresh neuf associé à l'ancien
 * access, ou l'inverse. [accessExpiresAtEpochSeconds] permet au réseau de rafraîchir de façon
 * proactive sans décoder le JWT côté client.
 */
@Serializable
data class Tokens(
    val accessToken: String,
    val refreshToken: String,
    val accessExpiresAtEpochSeconds: Long,
)

/**
 * Platform-agnostic interface for secure token storage.
 *
 * Une seule entrée par backend, jamais deux clés parallèles pour l'access et le refresh :
 * lire l'une sans l'autre exposerait une paire dépareillée. Platform implementations:
 * - Android: AES-256-GCM via AndroidKeyStore (#80)
 * - iOS: Keychain
 * - Desktop: OS-specific secure storage or encrypted file
 * - Web: localStorage (exposition XSS assumée et actée, ADR 0022)
 */
interface TokenStorage {
    /** Enregistre la paire courante, en remplaçant toute paire précédente. */
    suspend fun save(tokens: Tokens)

    /** Lit la paire stockée, ou `null` si absente ou dans un format irrécupérable. */
    suspend fun load(): Tokens?

    /** Purge la paire courante (logout, session expirée). */
    suspend fun clear()
}

/**
 * Platform-specific factory for creating TokenStorage.
 * Each platform provides its own implementation.
 */
expect fun createTokenStorage(): TokenStorage

/** Format d'échange du backend de stockage : une entrée JSON unique plutôt que des clés éclatées. */
internal val tokenStorageJson = Json { ignoreUnknownKeys = true }

internal fun Tokens.encode(): String = tokenStorageJson.encodeToString(Tokens.serializer(), this)

/**
 * `null` sur tout contenu irrécupérable : l'ancien format (jeton JWT seul, pas de JSON) comme un
 * JSON corrompu tombent dans le même cas, à charge de l'appelant de purger l'entrée périmée.
 */
internal fun decodeTokens(raw: String?): Tokens? =
    raw?.let { runCatching { tokenStorageJson.decodeFromString(Tokens.serializer(), it) }.getOrNull() }
